import { DEFAULT_LOCALE } from "@/constants/locales"

export const RESERVATION_DURATION_STEP_MINUTES = 30
export const RESERVATION_DURATION_STEP_SECONDS = RESERVATION_DURATION_STEP_MINUTES * 60

// Fallback ceiling when the venue has no `maxReservationTime` configured: the chooser is a mandatory
// CTA gate, so a single lone chip is a poor experience. 4h matches the mock's largest duration chip.
export const DEFAULT_MAX_RESERVATION_SECONDS = 4 * 60 * 60

export type ReservationDurationOption = {
  seconds: number
  minutes: number
  label: string
}

type ReservationDurationOptionsInput = {
  minReservationTime?: number | null
  maxReservationTime?: number | null
  locale?: string
}

/**
 * Compact, locale-aware hours label for a duration chip, e.g. `1,5h` (pl) / `1.5h` (en) / `2h`.
 * Only the decimal separator is locale-dependent; the `h` unit is shared across supported locales.
 */
export const formatDurationHoursLabel = (minutes: number, locale: string = DEFAULT_LOCALE): string => {
  const hours = minutes / 60
  const formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 })

  return `${formatter.format(hours)}h`
}

/**
 * Build the ordered duration chooser from the venue's reservation configuration.
 * Options run from `minReservationTime` to `maxReservationTime` (inclusive) in 30-minute steps.
 * Each option carries the raw `minutes` count (for a "90 min"-style subtitle) and a compact,
 * locale-aware `label` (for the chip headline). Always returns at least one option.
 */
export const getReservationDurationOptions = ({
  minReservationTime,
  maxReservationTime,
  locale = DEFAULT_LOCALE,
}: ReservationDurationOptionsInput): ReservationDurationOption[] => {
  const minSeconds =
    minReservationTime && minReservationTime > 0 ? minReservationTime : RESERVATION_DURATION_STEP_SECONDS
  // When max is set (and ≥ min) honour it; when it is unset fall back to a 4h ceiling (never below
  // min). A max that is set but below min is a misconfiguration → collapse to the single min option.
  const hasMax = Boolean(maxReservationTime && maxReservationTime > 0)
  const ceiling = hasMax ? maxReservationTime : Math.max(minSeconds, DEFAULT_MAX_RESERVATION_SECONDS)
  const maxSeconds = ceiling >= minSeconds ? ceiling : minSeconds

  const options: ReservationDurationOption[] = []

  for (let seconds = minSeconds; seconds <= maxSeconds; seconds += RESERVATION_DURATION_STEP_SECONDS) {
    const minutes = Math.round(seconds / 60)
    options.push({ seconds, minutes, label: formatDurationHoursLabel(minutes, locale) })
  }

  return options
}
